import axios, { AxiosResponse } from 'axios';

import { RequestHeaders } from '../types/common/HeaderTypes';
import {
    GetEstimateResponse,
    SaveEstimateApiRequest,
    SaveEstimateApiResponse,
    SaveEstimateMailApiRequest,
    SaveEstimateMailApiResponse,
    DeviceList as ApiResDeviceList,
    ESTIMATE_API_ERRORS,
} from '../types/api/EstimateApiType';
import { STARTUP_API_ERRORS } from '../types/api/StartApiType';
import {
    DeviceInfoRow,
    DeviceInfoInputRow,
    EstimatePattern,
    EstimateInquiry,
} from '../types/common/EstimateInquiryType';

import { AppDispatch } from '../store/store';
import { initializeApp } from './init';
import { setState as setEstimateInquiry } from '../slice/EstimateInquirySlice';

// 一時保存の見積問合せデータの変換処理(API→画面)
const apiResponseToPageEstData = (estimateResponse: GetEstimateResponse): EstimateInquiry => {
    // 機器情報
    const deviceList: Array<DeviceInfoRow> = [];
    estimateResponse.device_list?.forEach((deviceRow) => {
        // 機器行を作成
        const deviceInfoRow: DeviceInfoRow = {
            deviceRowNumber: deviceRow.device_row_number,
            modelid: deviceRow.estq_id,
            makerName: deviceRow.maker_name ?? '',
            modelName: deviceRow.mp_product_name ?? '',
            searchWord: deviceRow.searchword ?? '',
            modelType: deviceRow.model_number ?? '',
            inputList: [],
        };
        deviceList.push(deviceInfoRow);
        // 編集行を作成
        deviceRow.input_list.forEach((input_list) => {
            const inputRow: DeviceInfoInputRow = {
                deviceRowNumber: deviceRow.device_row_number,
                inputRowNumber: input_list.input_row_number,
                prefectureId: input_list.prefecture_id?.toString(),
                prefectureName: '',
                cityId: input_list.city_id?.toString() ?? '',
                cityName: '',
                unitCount: input_list.unit_count?.toString(),
                freeText: input_list.freeword ?? '',
            };

            deviceInfoRow.inputList.push(inputRow);
        });
    });
    // 見積り条件
    const estimatePatternList: Array<EstimatePattern> = [];
    estimateResponse.estimate_pattern_list?.forEach((estPatternRow, index) => {
        let checkCount = 0;
        let RowCount = 0;
        estPatternRow.estimate_list.forEach((edit_row) => {
            if (edit_row.check_flag === 1) {
                checkCount++;
            }
            RowCount++;
        });
        const estimatePattern: EstimatePattern = {
            estimatePatternRowNumber: index + 1,
            estimatePatternName: estPatternRow.pattern_name,
            estimateList: [],
            checkAllFlag: checkCount === RowCount,
        };
        // 見積詳細を追加
        estimatePattern.estimateList = estPatternRow.estimate_list.map((estimate) => {
            return {
                deviceRowNumber: estimate.device_row_number,
                inputRowNumber: estimate.input_row_number,
                startYear: estimate.mainte_start_year,
                startMonth: estimate.mainte_start_month,
                endYear: estimate.mainte_end_year,
                endMonth: estimate.mainte_end_month,
                maintenanceLevelCode: estimate.sla_id,
                checkFlag: estimate.check_flag === 1 ? true : false,
            };
        });
        estimatePatternList.push(estimatePattern);
    });

    return {
        deviceList: deviceList,
        estimatePatternList: estimatePatternList,
    };
};

// 一時保存取得　API-5001
const getDraftEstimate = async (draftId: string, dispatch: AppDispatch) => {
    try {
        const result: AxiosResponse<GetEstimateResponse> = await axios.get(`/api/estimate/get/${draftId}`);
        if (result.data.status) {
            // 画面データへの変換処理
            const draftEstimateInquiry: EstimateInquiry = apiResponseToPageEstData(result.data);
            // 取得内容でstoreを更新
            dispatch(setEstimateInquiry(draftEstimateInquiry));
            return '';
        } else {
            return ESTIMATE_API_ERRORS.REQUEST_ERROR_5001;
        }
    } catch (error) {
        // リクエストが異常終了
        let error_ret = ESTIMATE_API_ERRORS.REQUEST_ERROR_5001;
        if (axios.isAxiosError(error)) {
            if (error.response?.status) {
                // ステータスコードが設定されていた場合、ステータスコードを設定
                error_ret = ESTIMATE_API_ERRORS.REQUEST_ERROR_5001_STATUS_CODE.replace(
                    '%1',
                    error.response.status as unknown as string
                );
            }
        }

        return error_ret;
    }
};

// 見積り依頼登録　API-5002
const saveEstimate = async (
    estimateInquiry: EstimateInquiry,
    dispatch: AppDispatch,
    clientId: string,
    setDraftId?: (value: string) => void,
    tempFlg?: boolean
) => {
    // 初期API判定
    let initApiResult = '';
    if (clientId === '' || clientId === undefined) {
        initApiResult = await initializeApp(dispatch, clientId);
        if (initApiResult.includes(STARTUP_API_ERRORS.REQUEST_ERROR_8001)) {
            // initApiResultが異常終了した場合
            return initApiResult;
        }
    }

    // 見積り依頼データの編集
    const saveDraftData: SaveEstimateApiRequest = pageEstDataToApiRequest(estimateInquiry, tempFlg);
    // リクエストヘッダ設定
    const headers: RequestHeaders = {
        'client-id': clientId !== '' ? clientId : initApiResult,
    };

    try {
        const result: AxiosResponse<SaveEstimateApiResponse> = await axios.post('/api/estimate/save', saveDraftData, {
            headers: headers,
        });
        if (result.data) {
            if (setDraftId) {
                setDraftId(result.data.inquiry_estimate_id);
            }
            return result.data.inquiry_estimate_id;
        } else {
            return ESTIMATE_API_ERRORS.REQUEST_ERROR_5002;
        }
    } catch (error) {
        // リクエストが異常終了
        let error_ret = ESTIMATE_API_ERRORS.REQUEST_ERROR_5002;
        if (axios.isAxiosError(error)) {
            if (error.response?.status) {
                // ステータスコードが設定されていた場合、ステータスコードを設定
                error_ret = ESTIMATE_API_ERRORS.REQUEST_ERROR_5002_STATUS_CODE.replace(
                    '%1',
                    error.response.status as unknown as string
                );
            }
        }

        return error_ret;
    }
};

// 見積り依頼データの編集(画面→API)
const pageEstDataToApiRequest = (estimateInquiry: EstimateInquiry, temp_save_flag?: boolean) => {
    return {
        temp_save_flag: temp_save_flag ? 1 : 0,
        device_list: estimateInquiry.deviceList.map((device) => {
            const estqId = device.modelid !== undefined ? { estq_id: device.modelid } : {};
            const searchWord = device.searchWord ? { searchword: device.searchWord } : {};
            const modelNumber = device.modelType ? { model_number: device.modelType } : {};

            const data: ApiResDeviceList = {
                device_row_number: device.deviceRowNumber,
                ...estqId,
                ...searchWord,
                ...modelNumber,
                input_list: device.inputList.map((input) => {
                    return {
                        input_row_number: input.inputRowNumber,
                        prefecture_id: Number(input.prefectureId),
                        city_id: Number(input.cityId),
                        unit_count: Number(input.unitCount),
                        freeword: input.freeText,
                    };
                }),
            };
            return data;
        }),
        estimate_pattern_list: estimateInquiry.estimatePatternList.map((pattern) => {
            return {
                pattern_name: pattern.estimatePatternName ?? '',
                estimate_list: pattern.estimateList.map((estimate) => {
                    return {
                        device_row_number: estimate.deviceRowNumber,
                        input_row_number: estimate.inputRowNumber,
                        mainte_start_year: estimate.startYear,
                        mainte_start_month: estimate.startMonth,
                        mainte_end_year: estimate.endYear,
                        mainte_end_month: estimate.endMonth,
                        sla_id: estimate.maintenanceLevelCode,
                        check_flag: estimate.checkFlag ? 1 : 0,
                    };
                }),
            };
        }),
    } as SaveEstimateApiRequest;
};

// 一時保存メール送信 API-5003
const saveDraftEstimateMail = async (draftPath: string, draftId: string, sendMailAddress: string) => {
    const sendData: SaveEstimateMailApiRequest = {
        draft_path: draftPath,
        inquiry_estimate_id: draftId,
        mail: sendMailAddress,
    };
    try {
        const result: AxiosResponse<SaveEstimateMailApiResponse> = await axios.post('/api/estimate/mail', sendData);
        if (result.data) {
            return result.data.status;
        } else {
            return false;
        }
    } catch (error) {
        // リクエストが異常終了
        let error_ret = ESTIMATE_API_ERRORS.REQUEST_ERROR_5003;
        if (axios.isAxiosError(error)) {
            if (error.response?.status) {
                // ステータスコードが設定されていた場合、ステータスコードを設定
                error_ret = ESTIMATE_API_ERRORS.REQUEST_ERROR_5003_STATUS_CODE.replace(
                    '%1',
                    error.response.status as unknown as string
                );
            }
        }

        return error_ret;
    }
};

export const estimateApi = {
    getDraftEstimate,
    saveEstimate,
    saveDraftEstimateMail,
};
